import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import {
  ApplicationInsights,
  ICustomProperties,
  IEventTelemetry,
  IExceptionTelemetry,
  ITelemetryItem,
  ITraceTelemetry,
} from '@microsoft/applicationinsights-web';
import { AppConfigService } from '../../app-config/app-config.service';

@Injectable({
  providedIn: 'root',
})
export class InsightsService {
  private appConfigService = inject(AppConfigService);
  private router = inject(Router);

  private angularPlugin = new AngularPlugin();
  private appInsights = new ApplicationInsights({
    config: {
      connectionString:
        this.appConfigService?.config?.appInsightsConnectionString,
      extensionConfig: {
        [this.angularPlugin.identifier]: {
          errorServices: [new ErrorHandler()],
          router: this.router,
        },
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      extensions: [this.angularPlugin as any],
      isBeaconApiDisabled: false,
    },
  });
  private readonly clientInstanceId = this.generateClientInstanceId();

  constructor() {
    this.appInsights.loadAppInsights();
    this.addTelemetryInitializer();
  }

  clearAuthenticatedUserContext() {
    this.appInsights.clearAuthenticatedUserContext();
  }

  setAuthenticatedUserContext(userId: string) {
    this.appInsights.setAuthenticatedUserContext(userId);
  }

  trackEvent(
    name: IEventTelemetry['name'],
    customProperties?: ICustomProperties
  ): void {
    this.appInsights.trackEvent({ name }, customProperties);
  }

  trackException(
    exception: IExceptionTelemetry,
    customProperties?: ICustomProperties
  ): void {
    this.appInsights.trackException(exception, customProperties);
  }

  trackTrace(
    trace: ITraceTelemetry,
    customProperties?: ICustomProperties
  ): void {
    this.appInsights.trackTrace(trace, customProperties);
  }

  private addTelemetryInitializer(): void {
    this.appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
      item.data = item.data || {};
      item.data['clientInstanceId'] = this.clientInstanceId; // Uniquely identify which instance of the app sent this telemetry (tab/window).
      return true;
    });
  }

  private generateClientInstanceId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
  }
}
